import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Enhanced error handling utilities
class APIError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.success = false;
  }
}

// The API error envelope is {success:false, message, error, details} where
// `message` is a coarse category ("Access denied", "Invalid request") and
// `error` is the actionable reason ("You can only update your own profile").
// Prefer the specific one.
const serverMessage = (data) => data?.error || data?.message || null;

/**
 * Extract a human-readable message from a rejected API call.
 *
 * Use this instead of `err.response?.data?.error` -- requests are rejected with
 * an APIError, which has no `.response` property, so that pattern is always
 * undefined and silently falls through to a generic fallback.
 */
export const apiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;
  return serverMessage(error.details) || error.message || fallback;
};

// Network error detection
const isNetworkError = (error) => {
  return !error.response && error.request;
};

// Timeout error detection
const isTimeoutError = (error) => {
  return error.code === 'ECONNABORTED' || error.message.includes('timeout');
};

// Server error detection (5xx)
const isServerError = (error) => {
  return error.response && error.response.status >= 500;
};

// Only GET requests are idempotent and safe to retry automatically
const isIdempotentRequest = (error) => {
  const method = error.config?.method?.toUpperCase();
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
};

// Retry-able error detection: network/timeout/server errors on idempotent requests only
const isRetryableError = (error) => {
  return isIdempotentRequest(error) && (isNetworkError(error) || isTimeoutError(error) || isServerError(error));
};

// Retry with exponential backoff
const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on last attempt or non-retryable errors
      if (attempt === maxRetries || !isRetryableError(error)) {
        break;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Create axios instance with enhanced configuration
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Enhanced request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(new APIError('Request configuration failed', 0, 'REQUEST_CONFIG_ERROR', error));
  }
);

// Enhanced response interceptor with detailed error handling
api.interceptors.response.use(
  (response) => {
    // Any successful write to an events resource makes the cached event lists
    // stale. Invalidating centrally here means a new call site can't forget to
    // do it -- previously nothing ever called clearApiCache(), so a freshly
    // created CliQQ stayed missing from My Page for the full 5-minute TTL.
    const method = response.config?.method?.toUpperCase();
    const url = response.config?.url || '';
    if (method && method !== 'GET' && url.includes('/api/events')) {
      clearApiCache();
    }
    return response;
  },
  (error) => {
    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      timestamp: new Date().toISOString()
    };

    if (error.response) {
      // Server responded with error status
      const { status, statusText, data } = error.response;
      errorDetails.status = status;
      errorDetails.statusText = statusText;
      errorDetails.data = data;      // Create detailed error messages based on status codes
      let message = 'An error occurred';
      let code = 'UNKNOWN_ERROR';
      
      switch (status) {
        case 400:
          message = serverMessage(data) || 'Invalid request. Please check your input.';
          code = 'BAD_REQUEST';
          break;
        case 401:
          message = 'Your session has expired. Please log in again.';
          code = 'UNAUTHORIZED';
          // Clear the invalid credentials AND tell the app, otherwise
          // AuthContext keeps `currentUser` set and the UI goes on rendering
          // authenticated views whose every request now fails identically.
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth:expired'));
          break;
        case 403:
          message = serverMessage(data) || 'You do not have permission to perform this action.';
          code = 'FORBIDDEN';
          break;
        case 404:
          message = serverMessage(data) || 'The requested resource was not found.';
          code = 'NOT_FOUND';
          break;
        case 409:
          message = serverMessage(data) || 'Conflict: This action cannot be completed.';
          code = 'CONFLICT';
          break;
        case 422:
          message = serverMessage(data) || 'Validation failed. Please check your input.';
          code = 'VALIDATION_ERROR';
          break;
        case 429:
          message = 'Too many requests. Please wait before trying again.';
          code = 'RATE_LIMITED';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          code = 'SERVER_ERROR';
          break;
        case 502:
        case 503:
        case 504:
          message = 'Service temporarily unavailable. Please try again later.';
          code = 'SERVICE_UNAVAILABLE';
          break;
        default:
          message = serverMessage(data) || `Request failed with status ${status}`;
          code = `HTTP_${status}`;
      }
      
      return Promise.reject(new APIError(message, status, code, data));
    } else if (error.request) {
      // Network error
      let message = 'Network error. Please check your connection.';
      let code = 'NETWORK_ERROR';
      
      if (isTimeoutError(error)) {
        message = 'Request timed out. Please try again.';
        code = 'TIMEOUT_ERROR';
      }
      
      return Promise.reject(new APIError(message, 0, code, error.request));
    } else {
      // Request setup error
      return Promise.reject(new APIError('Request setup failed', 0, 'SETUP_ERROR', error.message));
    }
  }
);

// Enhanced API wrapper with retry logic
const apiRequest = async (requestFn, useRetry = true) => {
  if (useRetry) {
    return await retryRequest(requestFn);
  } else {
    return await requestFn();
  }
};

// Authentication API functions
export const login = (email, password) => {
  return apiRequest(() => api.post('/api/auth/login', { email, password }), false);
};

export const register = (userData) => {
  return apiRequest(() => api.post('/api/auth/register', userData), false);
};

// Event API functions with caching support
export const getEvents = (filters = {}, useCache = true) => {
  const cacheKey = `events_${JSON.stringify(filters)}`;
  if (useCache && sessionStorage.getItem(cacheKey)) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      if (cached.timestamp && Date.now() - cached.timestamp < 300000) { // 5 minutes
        // Return an axios-compatible shape so callers can use response.data consistently
        return Promise.resolve({ data: cached.data });
      } else {
        // Cache expired, remove it
        sessionStorage.removeItem(cacheKey);
      }
    } catch (e) {
      // Invalid cache data, remove it
      sessionStorage.removeItem(cacheKey);
    }
  }

  return apiRequest(() => api.get('/api/events', { params: filters }))
    .then(response => {
      if (useCache) {
        // Store only the response body, not the full axios response object
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      }
      return response;
    });
};

export const getEvent = (id) => {
  return apiRequest(() => api.get(`/api/events/${id}`));
};

export const createEvent = (eventData) => {  return apiRequest(() => api.post('/api/events', eventData), false); // Don't retry creation requests
};

export const attendEvent = (eventId) => {
  return apiRequest(() => api.post(`/api/events/${eventId}/register`), false);
};

export const cancelAttendance = (eventId) => {
  return apiRequest(() => api.delete(`/api/events/${eventId}/register`), false);
};

// Category API functions
export const getCategories = () => {
  return apiRequest(() => api.get('/api/categories'));
};

// ===== NEW SOCIAL API FUNCTIONS =====

// Social Summary (Main Endpoint)
export const getSocialSummary = () => {
  return apiRequest(() => api.get('/api/social/summary'));
};

// Friend Management (User Accounts Only)
export const getFriends = () => {
  return apiRequest(() => api.get('/api/social/friends'));
};

export const sendFriendRequest = (userId) => {
  return apiRequest(() => api.post('/api/social/friends/request', { user_id: userId }), false);
};

export const respondToFriendRequest = (friendshipId, action) => {
  return apiRequest(() => api.post('/api/social/friends/respond', { 
    friendship_id: friendshipId, 
    action: action 
  }), false);
};

export const getPendingFriendRequests = () => {
  return apiRequest(() => api.get('/api/social/friends/pending'));
};

export const getFriendEvents = (friendId) => {
  return apiRequest(() => api.get(`/api/social/friends/${friendId}/events`));
};

// Business Following
export const getFollowedBusinesses = () => {
  return apiRequest(() => api.get('/api/social/following'));
};

export const followBusiness = (businessId, action = 'follow') => {
  return apiRequest(() => api.post('/api/social/follow', { 
    business_id: businessId, 
    action: action 
  }), false);
};

export const unfollowBusiness = (businessId) => {
  return followBusiness(businessId, 'unfollow');
};

export const getBusinessEvents = (businessId) => {
  return apiRequest(() => api.get(`/api/social/businesses/${businessId}/events`));
};

// Search
export const searchSocial = (query, type = null) => {
  const params = { q: query };
  if (type) params.type = type;
  return apiRequest(() => api.get('/api/social/search', { params }));
};

export const searchUsers = (query) => {
  return searchSocial(query, 'user');
};

export const searchBusinesses = (query) => {
  return searchSocial(query, 'business');
};

// Organizations
export const getOrganizations = () => {
  return apiRequest(() => api.get('/api/organizations'));
};

export const createOrganization = (orgData) => {
  return apiRequest(() => api.post('/api/organizations', orgData), false);
};

export const joinOrganization = (organizationId) => {
  return apiRequest(() => api.post(`/api/organizations/${organizationId}/join`), false);
};

export const getOrganizationMembers = (organizationId) => {
  return apiRequest(() => api.get(`/api/organizations/${organizationId}/members`));
};

export const approveOrganizationMember = (membershipId, action) => {
  return apiRequest(() => api.post('/api/organizations/approve', { 
    membership_id: membershipId, 
    action: action 
  }), false);
};

// Event Approvals
export const requestEventApproval = (eventId) => {
  return apiRequest(() => api.post(`/api/approvals/events/${eventId}`), false);
};

export const getPendingEventApprovals = () => {
  return apiRequest(() => api.get('/api/approvals/events'));
};

export const respondToEventApproval = (approvalId, action) => {
  return apiRequest(() => api.post('/api/approvals/events/respond', { 
    approval_id: approvalId, 
    action: action 
  }), false);
};

export const getUserEventApprovalRequests = () => {
  return apiRequest(() => api.get('/api/approvals/user'));
};

// User Profile API functions
export const getUserProfile = (userId) => {
  return apiRequest(() => api.get(`/api/users/${userId}`));
};

// The user routes are `/api/users/:id`; there is no `/api/users/profile`
// endpoint -- posting to that literal path makes Gin bind id="profile" and the
// handler answers 403. The caller must pass the real user id.
export const updateUserProfile = (userId, profileData) => {
  return apiRequest(() => api.put(`/api/users/${userId}`, profileData), false);
};

// Group Cliqq API functions
export const getGroups = () => {
  return apiRequest(() => api.get('/api/groups'));
};

export const getGroup = (groupId) => {
  return apiRequest(() => api.get(`/api/groups/${groupId}`));
};

export const createGroup = (groupData) => {
  return apiRequest(() => api.post('/api/groups', groupData), false);
};

export const updateGroup = (groupId, groupData) => {
  return apiRequest(() => api.put(`/api/groups/${groupId}`, groupData), false);
};

export const deleteGroup = (groupId) => {
  return apiRequest(() => api.delete(`/api/groups/${groupId}`), false);
};

export const addEventToGroup = (groupId, eventId) => {
  return apiRequest(() => api.post(`/api/groups/${groupId}/events`, { event_id: eventId }), false);
};

export const removeEventFromGroup = (groupId, eventId) => {
  return apiRequest(() => api.delete(`/api/groups/${groupId}/events/${eventId}`), false);
};

// User Actions (Report, Block, Avoid)
export const reportUser = (reportedId, category, reason) => {
  return apiRequest(() => api.post('/api/user-actions/report', {
    reported_id: reportedId,
    category: category,
    reason: reason
  }), false);
};

export const blockUser = (blockedId, blockType, reason = '') => {
  return apiRequest(() => api.post('/api/user-actions/block', {
    blocked_id: blockedId,
    block_type: blockType,
    reason: reason
  }), false);
};

export const unblockUser = (userId) => {
  return apiRequest(() => api.delete(`/api/user-actions/block/${userId}`), false);
};

export const getBlockedUsers = (type = null) => {
  const params = type ? { type } : {};
  return apiRequest(() => api.get('/api/user-actions/blocked', { params }));
};

export const checkUserBlocked = (userId) => {
  return apiRequest(() => api.get(`/api/user-actions/blocked/${userId}`));
};

export const getUserReports = () => {
  return apiRequest(() => api.get('/api/user-actions/reports'));
};

// Notification API functions
export const getNotifications = () => {
  return apiRequest(() => api.get('/api/notifications'));
};

export const markNotificationsAsRead = (notificationIds) => {
  return apiRequest(() => api.put('/api/notifications/mark-read', { ids: notificationIds }), false);
};

export const deleteNotification = (notificationId) => {
  return apiRequest(() => api.delete(`/api/notifications/${notificationId}`), false);
};

// Health check and connection testing
export const healthCheck = () => {
  return apiRequest(() => api.get('/'), false); // No retry for health check
};

export const testConnection = async () => {
  try {
    const response = await healthCheck();
    return {
      status: 'connected',
      message: 'API connection successful',
      data: response.data
    };
  } catch (error) {
    return {
      status: 'disconnected',
      message: error.message,
      error: error
    };
  }
};

// Cache management utilities
export const clearApiCache = () => {
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('events_') || key.startsWith('social_')) {
      sessionStorage.removeItem(key);
    }
  });
};

// Export enhanced error class for use in components
export { APIError };

export default api; 